/**
 * Reading Travel Desk's machine error codes off an API failure (ADR 0030, `TRAVEL_ERROR_CODES`).
 *
 * The API sends the code in `body.details.code` (an `AppError` built as
 * `new ConflictError(msg, { code: 'stale_version' })`). `@ts-rest/react-query` throws a non-2xx as
 * `{ status, body, headers }`, so a screen holds that object in `error` / `onError`. Screens branch
 * on the code, never on the message.
 *
 * - `travelErrorCode(err)`: the `details.code` string, or `undefined`. Also reads
 *   `body.details.details.code`, the shape the module guard sends until it is aligned with the
 *   rest, so a screen does not care which spelling it gets.
 * - `isModuleDisabledError(err)`: true for the 404 `module_disabled` every guarded `/travel` route
 *   answers when the admin has not switched the module on (D33).
 * - `isStaleVersionError(err)`: true for a 409 `stale_version` (EC-51): someone else saved first;
 *   refetch, tell the person, and let them retry.
 */

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

export function travelErrorCode(err: unknown): string | undefined {
  if (!isRecord(err)) return undefined;
  const body = err['body'];
  if (!isRecord(body)) return undefined;
  const details = body['details'];
  if (!isRecord(details)) return undefined;
  const direct = details['code'];
  if (typeof direct === 'string') return direct;
  const nested = details['details'];
  if (isRecord(nested) && typeof nested['code'] === 'string') return nested['code'];
  return undefined;
}

export function isModuleDisabledError(err: unknown): boolean {
  return travelErrorCode(err) === 'module_disabled';
}

export function isStaleVersionError(err: unknown): boolean {
  return travelErrorCode(err) === 'stale_version';
}
